<template>
    <div class="animated fadeIn">
        <b-card class="piplines-list">
            <b-card-header class="card-header-main">
                <b-row class="piplines-list-header">
                    <b-col>
                        <strong>Permissions for project "{{ activeProject.name }}"</strong>
                    </b-col>
                    <b-col md="2">
                        <router-link to="/iam/permission/create">
                            <b-button variant="primary" class="text-nowrap">
                                <i class="icon-plus mr-1"></i>Add
                            </b-button>
                        </router-link>
                    </b-col>
                </b-row>
            </b-card-header>
            <b-card-body>
                <v-server-table
                    :url="table.apiUrl"
                    :columns="table.columns"
                    :options="table.options"
                    :theme="table.theme"
                    ref="permissionsTable"
                    class="server-table"
                >
                    <div slot="owner" slot-scope="props">
                        <span class="success-thumb">
                            <i v-if="props.row.owner" class="fa fa-thumbs-up success-thumb-up" />
                        </span>
                    </div>
                    <div slot="editorCount" slot-scope="props" class="button-row">
                        <span v-if="!props.row.owner">
                            {{ accessCount(props.row, 'Editor') }}
                        </span>
                    </div>
                    <div slot="viewerCount" slot-scope="props" class="button-row">
                        <span v-if="!props.row.owner">
                            {{ accessCount(props.row, 'Viewer') }}
                        </span>
                    </div>
                    <div slot="actions" slot-scope="props" class="button-row">
                        <span v-if="!props.row.owner">
                            <DetailsAction :routerLink="'/iam/permission/' + props.row.id" />
                            <RemoveAction :removeResource="showRemoveAccessModal" :resource="props.row" />
                        </span>
                    </div>
                </v-server-table>
            </b-card-body>
        </b-card>

        <b-card
            class="piplines-list"
            v-if="projectOwner && showInvitationTable"
            v-bind:class="{hidden: invitationTableHidden}"
        >
            <b-card-header class="card-header-main">
                <b-row class="piplines-list-header">
                    <b-col>
                        <strong>Invitations for project "{{ activeProject.name }}"</strong>
                    </b-col>
                </b-row>
            </b-card-header>
            <b-card-body>
                <v-server-table
                    :url="invitationTable.apiUrl"
                    :columns="invitationTable.columns"
                    :options="invitationTable.options"
                    :theme="invitationTable.theme"
                    ref="invitationTable"
                    class="server-table"
                >
                    <div slot="expires at" slot-scope="props">
                        {{ parseLocalDate(props.row.expiresAt) }}
                    </div>
                    <div slot="actions" slot-scope="props" class="button-row">
                        <RemoveAction :removeResource="showRemoveInvitationModal" :resource="props.row" />
                    </div>
                </v-server-table>
            </b-card-body>
        </b-card>

        <RemoveRecordModal
            v-model="showRemoveModal"
            :removeElement="removeRecord"
            :recordId="removeRecordId"
            @close="removeRecordId = null"
        />

        <Loading :loading="$store.state.loading.processing" :text="$store.state.loading.text"></Loading>
    </div>
</template>

<script>
import store from '../../../store/index'
import Vue from 'vue'
import {ServerTable} from 'vue-tables-2'
Vue.use(ServerTable)

import {mapGetters} from 'vuex'
import EventBus from '../../../eventBus'

import Loading from '@/components/loading.vue'
import Feedback from '@/components/Feedback.vue'
import RemoveRecordModal from '@/components/RemoveRecordModal.vue'

import DetailsAction from '@/components/Actions/DetailsAction.vue'
import RemoveAction from '@/components/Actions/RemoveAction.vue'

export default {
    template: 'default',
    data() {
        return {
            showRemoveModal: false,
            removeRecordId: null,
            accessError: {
                state: true,
                message: '',
            },
            table: {
                apiUrl: `${process.env.VUE_APP_NODE_API_HOST}/permissions`,
                columns: ['member', 'email', 'owner', 'editorCount', 'viewerCount', 'actions'],
                theme: 'bootstrap4',
                options: {
                    requestFunction: function(data) {
                        return this.axios
                            .get(this.url, {
                                params: {
                                    ...data,
                                    projectId: this.$store.state.project.active.id,
                                },
                            })
                            .catch(
                                function(e) {
                                    this.dispatch('error', e)
                                }.bind(this)
                            )
                    },
                    responseAdapter: function(resp) {
                        const data = resp.data
                        return {
                            data: data.data,
                            count: data.metadata.count,
                        }
                    },
                    orderBy: {
                        ascending: false,
                    },
                    filterable: false,
                    sortable: [],
                    rowClassCallback: () => 'main-row',
                },
            },
            invitationTable: {
                apiUrl: `${process.env.VUE_APP_NODE_API_HOST}/invitations`,
                columns: ['email', 'expires at', 'actions'],
                theme: 'bootstrap4',
                options: {
                    requestFunction: function(data) {
                        return this.axios
                            .get(this.url, {
                                params: {
                                    ...data,
                                    projectId: this.$store.state.project.active.id,
                                },
                            })
                            .catch(
                                function(e) {
                                    this.dispatch('error', e)
                                }.bind(this)
                            )
                    },
                    responseAdapter: function(resp) {
                        const data = resp.data

                        if (data.metadata.count === 0) {
                            this.$parent.showInvitationTable = false
                        } else {
                            this.$parent.invitationTableHidden = false
                        }

                        return {
                            data: data.data.map(el => ({...el, email: el.emailInvited})),
                            count: data.metadata.count,
                        }
                    },
                    orderBy: {
                        ascending: false,
                    },
                    filterable: false,
                    sortable: [],
                    rowClassCallback: () => 'main-row',
                },
            },
            showInvitationTable: true,
            invitationTableHidden: true,
            modalMode: 'access',
        }
    },
    computed: {
        ...mapGetters({
            activeProject: 'project/active',
        }),
        projectOwner() {
            return this.$store.state.project.active.userRef.id === this.$auth.user().id
        },
    },
    mounted() {
        EventBus.$on('projectChange', payload => {
            if (this.$refs.permissionsTable) {
                this.$refs.permissionsTable.refresh()
            }
        })
    },
    components: {
        ServerTable,
        Loading,
        RemoveRecordModal,
        DetailsAction,
        RemoveAction,
    },
    methods: {
        accessCount(permission, role) {
            if (permission.access) {
                const accessAll = permission.access.find(el => el.role === role && el.all)
                if (accessAll) {
                    return 'All'
                }
                return permission.access.filter(el => el.role === role).length
            } else return 0
        },
        removeRecord() {
            if (this.modalMode === 'access') {
                this.removeAccess()
            } else {
                this.removeInvitation()
            }
        },
        removeAccess() {
            const data = {projectId: this.$store.state.project.active.id}
            this.$store.commit('loading/PROCESSING', `Removing access...`)
            this.axios
                .delete(`${process.env.VUE_APP_NODE_API_HOST}/permissions/${this.removeRecordId}`)
                .then(response => {
                    this.showRemoveModal = false
                    this.$store.commit('loading/PROCESSED')
                    this.$refs['permissionsTable'].refresh()
                })
                .catch(exception => {
                    this.$store.commit('loading/PROCESSED')
                    this.accessError.state = false
                    this.accessError.message = exception.response.data.additionalInfo
                })
        },
        removeInvitation() {
            this.$store.commit('loading/PROCESSING', `Removing the invitation...`)
            this.axios
                .delete(`${process.env.VUE_APP_NODE_API_HOST}/invitations/${this.removeRecordId}`)
                .then(response => {
                    this.showRemoveModal = false
                    this.$store.commit('loading/PROCESSED')
                    this.$refs['invitationTable'].refresh()
                })
                .catch(exception => {
                    this.$store.commit('loading/PROCESSED')
                    this.accessError.state = false
                    this.accessError.message = exception.response.data.additionalInfo
                })
        },
        showRemoveAccessModal(access) {
            this.removeAccessId = access.id
            this.showRemoveModal = true
        },
        openUpdateAccessModal(access) {
            this.updateRow.id = access.id
            this.updateRow.role = access.role
            this.showUpdateAccessModal = true
        },
        parseLocalDate(_date) {
            const date = new Date(_date)
            return `${date.toLocaleDateString('pl-PL')} ${date.toLocaleTimeString('pl-PL')}`
        },
        showRemoveAccessModal(access) {
            this.removeRecordId = access.id
            this.modalMode = 'access'
            this.showRemoveModal = true
        },
        showRemoveInvitationModal(invitation) {
            this.removeRecordId = invitation.id
            this.modalMode = 'invitation'
            this.showRemoveModal = true
        },
    },
}
</script>

<style lang="scss">
.success-thumb {
    padding-left: 1.2rem;
}
.success-thumb-up {
    color: green;
}
</style>
